import { FormEvent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import agent from '../../app/api/agent';
import './SupportWidget.css';

export default function SupportWidget({ shopDomain, defaultEmail }: { shopDomain: string; defaultEmail?: string | null }) {
    const { pathname } = useLocation();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState(defaultEmail ?? '');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const launcher = useRef<HTMLButtonElement>(null);
    const emailInput = useRef<HTMLInputElement>(null);
    const messageInput = useRef<HTMLTextAreaElement>(null);
    const success = useRef<HTMLHeadingElement>(null);
    const submitting = useRef(false);
    const emailEdited = useRef(false);

    useEffect(() => {
        if (!emailEdited.current && !submitting.current && !sent) setEmail(defaultEmail ?? '');
    }, [defaultEmail, sent]);

    useEffect(() => {
        if (open) {
            if (sent) success.current?.focus();
            else if (emailInput.current?.value) messageInput.current?.focus();
            else emailInput.current?.focus();
        }
    }, [open, sent]);

    const close = () => {
        setOpen(false);
        launcher.current?.focus();
    };

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        if (submitting.current) return;
        const nextErrors: Record<string, string> = {};
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) nextErrors.email = 'Enter a valid email address.';
        if (!message.trim()) nextErrors.message = 'Tell us how we can help.';
        setErrors(nextErrors);
        setError('');
        if (Object.keys(nextErrors).length) {
            (nextErrors.email ? emailInput : messageInput).current?.focus();
            return;
        }

        submitting.current = true;
        setSending(true);
        try {
            await agent.Support.submit({ email: email.trim(), message: message.trim(), page: pathname });
            setSent(true);
            setMessage('');
        } catch (failure: any) {
            const status = failure?.response?.status;
            setError(status === 429
                ? 'You’ve sent several requests recently. Please wait a few minutes and try again.'
                : status === 401
                    ? 'Your session has expired. Reopen the app and try again.'
                    : 'We couldn’t send your request. Your message is still here. Please try again shortly.');
        } finally {
            submitting.current = false;
            setSending(false);
        }
    };

    return (
        <div className="SupportWidget">
            {open && (
                <section className="SupportWidget__panel" id="partfits-support-panel" role="dialog"
                    aria-labelledby="partfits-support-title" aria-describedby="partfits-support-description"
                    onKeyDown={event => {
                        if (event.key === 'Escape') { event.stopPropagation(); close(); }
                    }}>
                    <header className="SupportWidget__header">
                        <h2 id="partfits-support-title">How can we help?</h2>
                        <button type="button" className="SupportWidget__close" onClick={close} aria-label="Close support">
                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6" /></svg>
                        </button>
                    </header>
                    {sent ? (
                        <div className="SupportWidget__success" role="status">
                            <span className="SupportWidget__check" aria-hidden="true">✓</span>
                            <h3 ref={success} tabIndex={-1}>Request sent</h3>
                            <p id="partfits-support-description">Thanks for reaching out. We’ll reply to <strong>{email.trim()}</strong> by email.</p>
                            <button type="button" className="SupportWidget__submit" onClick={close}>Done</button>
                            <button type="button" className="SupportWidget__another" onClick={() => setSent(false)}>Send another request</button>
                        </div>
                    ) : (
                        <form className="SupportWidget__form" onSubmit={submit} noValidate aria-busy={sending}>
                            <p id="partfits-support-description" className="SupportWidget__description">Send us a message and we’ll get back to you by email.</p>
                            <div className="SupportWidget__field">
                                <label htmlFor="partfits-support-email">Your email</label>
                                <input id="partfits-support-email" ref={emailInput} type="email" autoComplete="email" required maxLength={254}
                                    value={email} onChange={event => { emailEdited.current = true; setEmail(event.target.value); }} disabled={sending}
                                    placeholder="you@example.com" aria-invalid={!!errors.email}
                                    aria-describedby={errors.email ? 'partfits-support-email-error' : undefined} />
                                {errors.email && <p className="SupportWidget__fieldError" id="partfits-support-email-error">{errors.email}</p>}
                            </div>
                            <div className="SupportWidget__field">
                                <label htmlFor="partfits-support-message">Message</label>
                                <textarea id="partfits-support-message" ref={messageInput} required maxLength={5000} rows={4}
                                    value={message} onChange={event => setMessage(event.target.value)} disabled={sending}
                                    placeholder="Tell us what’s happening and how we can help…" aria-invalid={!!errors.message}
                                    aria-describedby={errors.message ? 'partfits-support-message-error' : undefined} />
                                {errors.message && <p className="SupportWidget__fieldError" id="partfits-support-message-error">{errors.message}</p>}
                            </div>
                            <p className="SupportWidget__context">Your store ({shopDomain}) and current app page are included.</p>
                            {error && <p className="SupportWidget__error" role="alert">{error}</p>}
                            <button type="submit" className="SupportWidget__submit" disabled={sending}>{sending ? 'Sending…' : 'Send request'}</button>
                        </form>
                    )}
                </section>
            )}
            <button ref={launcher} type="button" className="SupportWidget__launcher" onClick={() => open ? close() : setOpen(true)}
                aria-label={open ? 'Close support' : 'Contact support'} aria-expanded={open} aria-controls="partfits-support-panel" aria-haspopup="dialog">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11.5a8 8 0 0 1-8 8 9 9 0 0 1-3.5-.7L4 20l1.2-4.5a9 9 0 0 1-.7-3.5 8 8 0 0 1 8-8H13a7 7 0 0 1 7 7v.5Z" /><path d="M8.5 10h7M8.5 14h4.5" /></svg>
                <span>Support</span>
            </button>
        </div>
    );
}
